import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ArrowLeft, MapPin, Building2, Users, GraduationCap, BookOpen,
  Shield, ExternalLink, Pencil, KeyRound,
} from 'lucide-react';
import { useImpersonate } from '../../hooks/useImpersonate';
import { GestorCard } from '../../components/gestao/GestorCard';
import { EscolaAcessosRapidosModal } from '../../components/gestao/EscolaAcessosRapidosModal';


const API = import.meta.env.VITE_API_URL || '/api/v1';
const getToken = () => localStorage.getItem('ac_token');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

/* ───────── Hooks ───────── */

function useMunicipioDetalhe(id: string) {
  return useQuery({
    queryKey: ['municipio-detalhe', id],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/municipios/${id}`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      return res.json();
    },
    enabled: !!id,
  });
}

function useMunicipioStats(id: string) {
  return useQuery({
    queryKey: ['municipio-stats', id],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/municipios/${id}/stats`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      return res.json();
    },
    enabled: !!id,
  });
}

function useMunicipioGestores(municipioId: string) {
  return useQuery({
    queryKey: ['municipio-gestores', municipioId],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/usuarios`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      const all = await res.json();
      const list = Array.isArray(all) ? all : all.data || [];
      return list.filter((u: any) => u.municipioId === municipioId && u.perfil === 'MUNICIPAL');
    },
    enabled: !!municipioId,
  });
}

function useMunicipioEscolas(municipioId: string) {
  return useQuery({
    queryKey: ['municipio-escolas', municipioId],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/escolas`, { headers: headers() });
      if (!res.ok) return [];
      const all = await res.json();
      const list = Array.isArray(all) ? all : all.data || [];
      return list.filter((e: any) => e.municipioId === municipioId);
    },
    enabled: !!municipioId,
  });
}

/* ───────── STAT CARD ───────── */

function StatCard({ icon, value, label, color }: { icon: React.ReactNode; value: number | string; label: string; color: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      background: 'rgba(255,255,255,.12)', borderRadius: 12, flex: 1, minWidth: 110,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}30`, color: 'white',
      }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 800, fontSize: '1.3rem', color: 'white', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '.68rem', color: 'rgba(255,255,255,.7)', fontWeight: 500, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

/* GestorCard importado de ../../components/gestao/GestorCard */

/* ───────── MAIN PAGE ───────── */

export function MunicipioDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: municipio, isLoading } = useMunicipioDetalhe(id || '');
  const { data: stats } = useMunicipioStats(id || '');
  const { data: gestores } = useMunicipioGestores(id || '');
  const { data: escolas } = useMunicipioEscolas(id || '');
  const { impersonate: handleImpersonate, impersonating } = useImpersonate();
  const [credenciaisEscola, setCredenciaisEscola] = useState<{ id: string; nome: string } | null>(null);


  if (isLoading) return <div className="admin-page animate-fadeIn" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>;
  if (!municipio) return <div className="admin-page animate-fadeIn" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Município não encontrado</div>;

  const tipoRedeColors: Record<string, string> = { MUNICIPAL: '#10b981', ESTADUAL: '#6366f1', PARTICULAR: '#f59e0b', FEDERAL: '#0ea5e9' };
  const escolaList: any[] = escolas || [];
  const gestorList: any[] = gestores || [];

  return (
    <>
    <div className="admin-page animate-fadeIn" style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* ═══ HERO ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af, #3b82f6, #60a5fa)',
        borderRadius: 20, padding: '28px 32px 24px', marginBottom: 24,
        color: 'white', position: 'relative', overflow: 'hidden',
      }}>
        {/* Dot pattern */}
        <div style={{ position: 'absolute', inset: 0, opacity: .06, backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, position: 'relative', zIndex: 1 }}>
          <button onClick={() => navigate('/master/municipios')} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.15)',
            border: 'none', color: 'white', padding: '6px 14px', borderRadius: 8,
            cursor: 'pointer', fontSize: '.78rem', fontWeight: 600,
          }}>
            <ArrowLeft size={14} /> Voltar para Municípios
          </button>
          <button onClick={() => navigate(`/master/municipios`, { state: { editId: id } })} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.15)',
            border: 'none', color: 'white', padding: '6px 14px', borderRadius: 8,
            cursor: 'pointer', fontSize: '.78rem', fontWeight: 600,
          }}>
            <Pencil size={14} /> Editar Município
          </button>
        </div>

        <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,.6)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <MapPin size={14} /> MUNICÍPIO
        </div>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 900, margin: '0 0 6px', lineHeight: 1.1 }}>
          {municipio.nome}
        </h1>
        <div style={{ fontSize: '.85rem', color: 'rgba(255,255,255,.75)', fontWeight: 500 }}>
          {municipio.estado?.nome} ({municipio.estado?.sigla}) · {municipio.estado?.rede?.nome || 'Sem Rede'}
          {municipio.codigoIBGE && <> · IBGE: {municipio.codigoIBGE}</>}
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
          <StatCard icon={<Building2 size={18} />} value={stats?.escolas?.total || escolaList.length} label="Escolas" color="#3b82f6" />
          <StatCard icon={<Users size={18} />} value={stats?.alunos?.total || 0} label="Alunos" color="#f59e0b" />
          <StatCard icon={<GraduationCap size={18} />} value={stats?.professores?.total || 0} label="Professores" color="#8b5cf6" />
          <StatCard icon={<BookOpen size={18} />} value={stats?.turmas?.total || 0} label="Turmas" color="#10b981" />
          <StatCard icon={<Shield size={18} />} value={gestorList.length} label="Gestores" color="#0ea5e9" />
        </div>
      </div>

      {/* ═══ CONTENT GRID ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── GESTORES ── */}
        <div style={{
          gridColumn: gestorList.length > 2 ? '1 / -1' : 'auto',
          padding: '20px 22px', borderRadius: 16,
          background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Shield size={18} style={{ color: '#3b82f6' }} />
            <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>Gestores Municipais</span>
            <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '.68rem', fontWeight: 700, background: '#3b82f610', color: '#3b82f6' }}>{gestorList.length}</span>
          </div>
          {gestorList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '.82rem', fontStyle: 'italic' }}>
              Nenhum gestor municipal cadastrado
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gestorList.map((g: any) => (
                <GestorCard key={g.id} user={g} onImpersonate={handleImpersonate} impersonating={impersonating} accentColor="#3b82f6" />
              ))}
            </div>
          )}
        </div>

        {/* ── ESCOLAS ── */}
        <div style={{
          gridColumn: escolaList.length > 3 ? '1 / -1' : 'auto',
          padding: '20px 22px', borderRadius: 16,
          background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Building2 size={18} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>Escolas</span>
            <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '.68rem', fontWeight: 700, background: '#10b98110', color: '#10b981' }}>{escolaList.length}</span>
          </div>
          {escolaList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '.82rem', fontStyle: 'italic' }}>
              Nenhuma escola vinculada
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {escolaList.map((e: any) => {
                const color = tipoRedeColors[e.tipoRede] || '#6b7280';
                return (
                  <div key={e.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)',
                    background: 'var(--bg-main, #f9fafb)', transition: 'all .15s',
                  }}
                    onMouseEnter={ev => { (ev.currentTarget as HTMLElement).style.borderColor = '#3b82f6'; (ev.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(59,130,246,.1)'; }}
                    onMouseLeave={ev => { (ev.currentTarget as HTMLElement).style.borderColor = ''; (ev.currentTarget as HTMLElement).style.boxShadow = ''; }}
                  >
                    <Building2 size={16} style={{ color: '#3b82f6', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nome}</div>
                      <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{e.codigo}</div>
                    </div>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.62rem', fontWeight: 700, background: `${color}15`, color }}>{e.tipoRede}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.62rem', fontWeight: 700, background: '#f59e0b12', color: '#f59e0b' }}>
                      {e._count?.alunos || 0} alunos
                    </span>
                    <Link to={`/master/escolas/${e.id}`} style={{
                      width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#6366f108', color: '#6366f1', border: '1px solid #6366f120', textDecoration: 'none',
                    }} title="Ver detalhe">
                      <ExternalLink size={13} />
                    </Link>
                    {e.isDemo && (
                      <button
                        onClick={() => setCredenciaisEscola({ id: e.id, nome: e.nome })}
                        title="Credenciais de acesso demo"
                        style={{
                          width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(99,102,241,0.08)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.2)',
                          cursor: 'pointer',
                        }}
                      >
                        <KeyRound size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── DETALHES ── */}
        <div style={{
          gridColumn: '1 / -1', padding: '20px 22px', borderRadius: 16,
          background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <MapPin size={18} style={{ color: '#6366f1' }} />
            <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>Detalhes do Município</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {[
              { label: 'Nome', value: municipio.nome },
              { label: 'Código IBGE', value: municipio.codigoIBGE || '—' },
              { label: 'Estado', value: municipio.estado ? `${municipio.estado.nome} (${municipio.estado.sigla})` : '—' },
              { label: 'Rede', value: municipio.estado?.rede?.nome || '—' },
              { label: 'Escolas Ativas', value: stats?.escolas?.ativos ?? '—' },
              { label: 'Alunos Ativos', value: stats?.alunos?.ativos ?? '—' },
              { label: 'Professores Ativos', value: stats?.professores?.ativos ?? '—' },
              { label: 'Turmas Ativas', value: stats?.turmas?.ativas ?? '—' },
              { label: 'Criado em', value: municipio.criadoEm ? new Date(municipio.criadoEm).toLocaleDateString('pt-BR') : '—' },
            ].map((item, i) => (
              <div key={i} style={{
                padding: '10px 14px', borderRadius: 10,
                background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
              }}>
                <div style={{ fontSize: '.62rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.5px' }}>{item.label}</div>
                <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--text-main)' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {credenciaisEscola && (
      <EscolaAcessosRapidosModal
        escolaId={credenciaisEscola.id}
        escolaNome={credenciaisEscola.nome}
        isOpen={true}
        onClose={() => setCredenciaisEscola(null)}
      />
    )}
    </>
  );
}
